import React, { useRef, useEffect, memo } from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import FreeSpinCounterController from "./FreeSpinCounterController";
import { safeInit } from "../../../../utilities";
import { selectFreeSpinCounterVisible } from "../../../../State/selectors";

/**
 * FreeSpinCounter
 * Renders the free spin counter spine animation.
 * Thin wrapper — all logic lives in FreeSpinCounterController.
 */
const FreeSpinCounter = () => {
  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const {
    symbolWidth,
    symbolHeight,
    dimensions,
    dividerDimensions,
    device,
    orientation,
  } = useDimensions();
  const freeSpinCounterVisible = useSelector(selectFreeSpinCounterVisible);

  // Initialize controller once
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new FreeSpinCounterController(
          containerRef.current,
          symbolWidth,
          symbolHeight,
          dimensions,
          dividerDimensions,
          device,
          orientation,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.resize(
        symbolWidth,
        symbolHeight,
        dimensions,
        dividerDimensions,
        device,
        orientation,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolHeight]);

  // Show/hide based on state
  useEffect(() => {
    if (!controllerRef.current) return;

    if (freeSpinCounterVisible) {
      controllerRef.current.show();
    } else {
      controllerRef.current.hide();
    }
  }, [freeSpinCounterVisible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
    };
  }, []);

  return (
    <Container
      ref={containerRef}
      name="free_spin_counter"
      x={0}
      y={0}
      zIndex={5}
      sortableChildren
    />
  );
};

export default memo(FreeSpinCounter);
